// Thin wrapper over `date-fns` so the rest of the app imports from one
// place. The Vietnamese locale + 24-hour conventions are encoded here.
//
// Why this module exists: agents juggle `Date | string | undefined`
// everywhere; centralising the formatters keeps the call sites
// declarative ("formatTime(x)") and the timezone behaviour reviewable
// in one file.

import { format, isValid, parseISO } from "date-fns";
import { vi } from "date-fns/locale";

/** Coerce a string or Date into a Date. Returns null when invalid. */
export function toDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/**
 * Long day label, e.g. "Thứ Năm, 12/05/2026". Used as the sticky day
 * header in the agenda view.
 */
export function formatDateLong(value: string | Date): string {
  const d = toDate(value);
  if (d === null) return "";
  return format(d, "EEEE, dd/MM/yyyy", { locale: vi });
}

/**
 * Compact day label, e.g. "T5 12/05". Used as the column header in the
 * week view where space is tight.
 */
export function formatDateShort(value: string | Date): string {
  const d = toDate(value);
  if (d === null) return "";
  return format(d, "EEEEEE dd/MM", { locale: vi });
}

/** Date-only key for grouping, e.g. "2026-05-12". */
export function dateKey(value: string | Date): string {
  const d = toDate(value);
  if (d === null) return "";
  return format(d, "yyyy-MM-dd");
}

/** 24-hour time, e.g. "09:00". */
export function formatTime(value: string | Date): string {
  const d = toDate(value);
  if (d === null) return "";
  return format(d, "HH:mm");
}

/** Returns YYYY-MM-DD for "today" in the user's local time. */
export function todayKey(now: Date = new Date()): string {
  return format(now, "yyyy-MM-dd");
}

/** Parse a YYYY-MM-DD string into a local-midnight Date. */
export function parseDateKey(key: string): Date | null {
  // parseISO("2026-05-12") creates a midnight UTC date, which then shifts
  // when displayed in local time. Build it explicitly in local time so
  // "12/05" stays "12/05".
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(
    Number.parseInt(y!, 10),
    Number.parseInt(m!, 10) - 1,
    Number.parseInt(d!, 10),
  );
  return isValid(date) ? date : null;
}

// Re-export the date-fns helpers we use elsewhere so callers don't need
// two import sites.
export {
  addDays,
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfWeek,
  isPast,
  isSameDay,
  isToday,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
